/**
 * ¿Cambió algo de la configuración del negocio respecto de lo guardado?
 *
 * Hermano de lib/horarios.js (`mismoHorario`), y existe por el mismo motivo:
 * el panel necesita saber si hay trabajo sin guardar para avisar antes de que
 * la persona se vaya, y `JSON.stringify(a) === JSON.stringify(b)` no sirve para
 * contestarlo.
 *
 * Acá el ruido es distinto al de los horarios. `businessInfo` es un objeto
 * grande que además del formulario carga cosas que el guardado ni manda
 * (`schedule`, `userType`, `slug`) y valores de relleno que el panel escribe
 * para que el formulario no arranque en blanco: `"Añadir dirección"`,
 * `"Añadir teléfono"`. Comparando el objeto entero, abrir el panel y no tocar
 * nada ya daba "tenés cambios sin guardar", y un aviso que aparece cuando no
 * hiciste nada es un aviso que se aprende a ignorar — que es exactamente lo que
 * no puede pasarle al que sí cambió algo.
 *
 * Por eso compara sólo los campos que el panel realmente manda al servidor
 * (la misma lista blanca de `handleSaveChanges`, que es la que decide qué se
 * guarda), y normaliza antes: los placeholders valen lo mismo que vacío, y
 * `null`/`undefined` también.
 */

/**
 * Los campos que viajan en `PUT /api/business/info`.
 *
 * Tiene que coincidir con la lista blanca de `handleSaveChanges` y con `CAMPOS`
 * de backend/src/contacto.js. Si se agrega un campo al formulario y no se
 * agrega acá, el campo se guarda pero salir sin guardarlo no avisa nada.
 */
export const CAMPOS_DEL_NEGOCIO = [
  'name',
  'email',
  'phone',
  'address',
  'street',
  'floor',
  'city',
  'postalCode',
  'province',
  'country',
  'description',
  'mensaje',
  'businessType',
];

/**
 * Textos que el panel pone para que el campo no se vea vacío. No son datos: si
 * el negocio nunca cargó el teléfono, `"Añadir teléfono"` y `''` son el mismo
 * estado, y tratarlos distinto haría aparecer cambios que nadie hizo.
 */
const RELLENOS = new Set(['Añadir dirección', 'Añadir teléfono', 'Añadir email']);

const normalizar = (valor) => {
  if (valor === null || valor === undefined) return '';
  const texto = String(valor).trim();
  return RELLENOS.has(texto) ? '' : texto;
};

/** Los campos guardables de `datos`, ya normalizados. */
export function datosComparables(datos) {
  const salida = {};
  for (const campo of CAMPOS_DEL_NEGOCIO) {
    salida[campo] = normalizar(datos?.[campo]);
  }
  return salida;
}

/** ¿Los dos describen la misma configuración? */
export function mismosDatosDelNegocio(a, b) {
  const uno = datosComparables(a);
  const otro = datosComparables(b);
  return CAMPOS_DEL_NEGOCIO.every((campo) => uno[campo] === otro[campo]);
}
